import type { QueryObserverResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

import { apiClient } from '@/lib/api';
import { createApiQueryHook } from '@/src/hooks/useApiQuery';

import { ADMIN_CONSENT_PROVIDERS } from '../lib/integration-constants';
import type { OAuthProvider } from '../types';

/**
 * Response body for the tenant pre-flight endpoint (M11-002 task 2.7).
 *
 * The envelope is unwrapped inside `apiClient.get`; this validates the cargo.
 */
export const tenantPreflightResponseSchema = z.object({
  provisioned: z.boolean(),
  provisionedAt: z.string().optional(),
  adminConsentUrl: z.string(),
});

export type TenantPreflightResponse = z.infer<
  typeof tenantPreflightResponseSchema
>;

interface TenantPreflightParams extends Record<string, unknown> {
  provider: OAuthProvider;
  tenantId: string | undefined;
}

/**
 * Fetches admin-consent pre-flight status for a provider tenant.
 *
 * `GET /api/integrations/[provider]/tenant-preflight?tenantId=...`
 */
export async function fetchTenantPreflight({
  provider,
  tenantId,
}: TenantPreflightParams): Promise<TenantPreflightResponse> {
  const query = `?tenantId=${encodeURIComponent(tenantId ?? '')}`;

  return apiClient.get<TenantPreflightResponse>(
    `/api/integrations/${provider}/tenant-preflight${query}`,
    tenantPreflightResponseSchema
  );
}

const useTenantPreflightQuery = createApiQueryHook<
  TenantPreflightResponse,
  TenantPreflightParams
>(fetchTenantPreflight, ({ provider, tenantId }) => [
  'tenant-preflight',
  provider,
  tenantId ?? '',
]);

export type TenantConsentStatusValue =
  | 'provisioned'
  | 'not_provisioned'
  | 'unknown';

export interface TenantConsentStatus {
  /**
   * Derived provisioning status. Loading, disabled, and error states all
   * resolve to `'unknown'` — the gate treats `'unknown'` as FAIL-OPEN.
   */
  status: TenantConsentStatusValue;
  /** Shareable "ask your IT admin" link; undefined until the query resolves. */
  adminConsentUrl: string | undefined;
  refetch: () => Promise<QueryObserverResult<TenantPreflightResponse, Error>>;
  isFetching: boolean;
}

/**
 * React Query hook exposing admin-consent pre-flight status for a tenant.
 *
 * Fetched on mount (not inside the click gesture) so the pre-flight gate can
 * read `status` synchronously at click time — popup blockers forbid awaiting a
 * network round-trip inside the click → `window.open` path.
 *
 * Enabled only for `ADMIN_CONSENT_PROVIDERS` with a non-empty tenantId; every
 * other case (disabled, loading, error) derives to `'unknown'` (fail-open).
 */
export function useTenantConsentStatus(
  provider: OAuthProvider,
  tenantId: string | undefined
): TenantConsentStatus {
  const enabled =
    ADMIN_CONSENT_PROVIDERS.includes(provider) && Boolean(tenantId);

  const { data, refetch, isFetching } = useTenantPreflightQuery(
    { provider, tenantId },
    { enabled, staleTime: 60_000 }
  );

  return useMemo<TenantConsentStatus>(() => {
    const status: TenantConsentStatusValue = data
      ? data.provisioned
        ? 'provisioned'
        : 'not_provisioned'
      : 'unknown';

    return {
      status,
      adminConsentUrl: data?.adminConsentUrl,
      refetch,
      isFetching,
    };
  }, [data, refetch, isFetching]);
}
